import { useState } from 'react';
import { Alert, Badge, Button, Card, Spinner, Table } from 'react-bootstrap';
import toast from 'react-hot-toast';
import type { BridgeTenant, RegisterBridgeTenantInput } from '../../../infrastructure/BridgeTenantClient';
import BrandHandoffDialog from './BrandHandoffDialog';
import RegisterBrandDialog from './RegisterBrandDialog';
import { useBridgeTenants } from './useBridgeTenants';

export default function BrandOnboardingPage() {
  const { tenants, loading, error, register, setStatus, remove } = useBridgeTenants();
  const [showRegister, setShowRegister] = useState(false);
  const [handoff, setHandoff] = useState<BridgeTenant | null>(null);

  const onRegister = async (input: RegisterBridgeTenantInput) => {
    const created = await register(input);
    setShowRegister(false);
    setHandoff(created);
  };

  const onToggleStatus = async (tenant: BridgeTenant) => {
    const next = tenant.status === 'active' ? 'suspended' : 'active';
    try {
      await setStatus(tenant.tenantId, next);
      toast.success(
        next === 'suspended'
          ? `${tenant.brandName} 수집 정지 — 캐시 때문에 최대 5분간 이미 캐시된 요청은 저장될 수 있다`
          : `${tenant.brandName} 수집 재개`,
      );
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : '상태 변경에 실패했습니다');
    }
  };

  const onRemove = async (tenant: BridgeTenant) => {
    const confirmed = window.confirm(
      `${tenant.brandName}을(를) 삭제합니다.\n\n이 브랜드가 수집한 이벤트도 함께 삭제되며 되돌릴 수 없습니다.\n계속하려면 확인을 누르세요.`,
    );
    if (!confirmed) {
      return;
    }
    try {
      await remove(tenant.tenantId);
      toast.success(`${tenant.brandName} 삭제됨`);
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : '삭제에 실패했습니다');
    }
  };

  return (
    <div className="container-fluid px-4 pb-4">
      <div className="text-muted small mb-1">B2B › Brand Onboarding</div>
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
        <div>
          <h1 className="h3 mb-1">Brand Onboarding</h1>
          <p className="text-muted mb-0" style={{ maxWidth: 640 }}>
            브릿지 페이지에서 이벤트를 보낼 수 있는 브랜드 등록부. 여기 없는 브랜드가 보낸 이벤트는 저장되지 않고
            버려진다.
          </p>
        </div>
        <Button variant="dark" onClick={() => setShowRegister(true)}>
          ＋ 브랜드 등록
        </Button>
      </div>

      {error && (
        <Alert variant="danger" className="py-2 px-3">
          {error}
        </Alert>
      )}

      <Card>
        <Card.Header className="d-flex align-items-center gap-2">
          <strong>등록된 브랜드</strong>
          <Badge bg="secondary" pill>
            {tenants.length}
          </Badge>
        </Card.Header>
        {loading ? (
          <Card.Body className="text-center text-muted py-5">
            <Spinner animation="border" size="sm" className="me-2" />
            불러오는 중…
          </Card.Body>
        ) : tenants.length === 0 ? (
          <Card.Body className="text-center text-muted py-5">
            아직 등록된 브랜드가 없다. <strong>＋ 브랜드 등록</strong>으로 시작한다.
          </Card.Body>
        ) : (
          <Table responsive hover className="mb-0 align-middle">
            <thead>
              <tr>
                <th>브랜드</th>
                <th>Shop 도메인</th>
                <th>허용 Origin</th>
                <th>상태</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {tenants.map((tenant) => (
                <tr key={tenant.tenantId}>
                  <td>
                    <div className="fw-medium">{tenant.brandName}</div>
                    <div className="text-muted font-monospace small">{tenant.tenantId}</div>
                  </td>
                  <td className="font-monospace small">{tenant.shopDomain ?? <span className="text-muted">—</span>}</td>
                  <td>
                    <Badge bg="light" text="dark" className="border fw-normal">
                      {tenant.allowedOrigins.length}개
                    </Badge>
                  </td>
                  <td>
                    <Badge bg={tenant.status === 'active' ? 'success' : 'secondary'} pill>
                      {tenant.status}
                    </Badge>
                  </td>
                  <td>
                    <div className="d-flex gap-2 justify-content-end">
                      <Button size="sm" variant="outline-secondary" onClick={() => setHandoff(tenant)}>
                        설정값
                      </Button>
                      <Button size="sm" variant="outline-secondary" onClick={() => onToggleStatus(tenant)}>
                        {tenant.status === 'active' ? '정지' : '재개'}
                      </Button>
                      <Button size="sm" variant="outline-danger" onClick={() => onRemove(tenant)}>
                        삭제
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <RegisterBrandDialog show={showRegister} onClose={() => setShowRegister(false)} onSubmit={onRegister} />
      {handoff && <BrandHandoffDialog tenant={handoff} onClose={() => setHandoff(null)} />}
    </div>
  );
}
